import { useLayoutEffect, useRef } from "react"
import { useTrackScrollOverflow } from "../use-track-scroll-overflow"
import {
  scrollFocusedItemIntoView,
  scrollItemClearOfFades
} from "./overflow-scroll-utils"

// =============================================================================
// Types
// =============================================================================

export type UseOverflowScrollOptions = {
  /**
   * The attribute marking the item to keep in view, such as `aria-current`.
   * That item is scrolled clear of the fades on mount and whenever the
   * attribute moves to another item.
   */
  activeAttribute?: string
  /**
   * Turns the behavior off while the items don't scroll, such as tabs laid out
   * vertically. The scroller then never reports overflow and gets no scroll
   * buttons.
   */
  disabled?: boolean
}

// =============================================================================
// Hook
// =============================================================================

/**
 * Horizontal overflow scrolling for a row of items, with fades and scroll
 * buttons at each edge with more content, and keyboard-focused items scrolled
 * into view.
 *
 * Spread `scrollerProps` on the scroller, which has `overflowScrollStyles`,
 * and render `OverflowScrollButtons` with `scrollButtonsProps` beside it,
 * inside an element with `overflowScrollContainerStyles`.
 */
export function useOverflowScroll<T extends HTMLElement>({
  activeAttribute,
  disabled = false
}: UseOverflowScrollOptions = {}) {
  const scrollerRef = useRef<T>(null)
  // The ref stays attached while disabled because the tracker reads it only on
  // mount, so a scroller that starts disabled still measures once enabled
  const trackedOverflow = useTrackScrollOverflow(scrollerRef)
  const hasLeftOverflow = !disabled && trackedOverflow.hasLeftOverflow
  const hasRightOverflow = !disabled && trackedOverflow.hasRightOverflow

  useLayoutEffect(() => {
    const scroller = scrollerRef.current
    if (!scroller || !activeAttribute || disabled) return

    const activeSelector = `[${activeAttribute}]`
    const scrollActiveItemClearOfFades = () => {
      const activeItem = scroller.querySelector(activeSelector)
      if (activeItem) scrollItemClearOfFades(scroller, activeItem)
    }
    scrollActiveItemClearOfFades()

    const isOrHoldsActiveItem = (node: Node) =>
      node instanceof Element &&
      (node.matches(activeSelector) || !!node.querySelector(activeSelector))

    // The attribute can move between items without re-rendering the scroller,
    // as Base UI's `data-active` does between tabs. An item can also arrive
    // already active when React swaps its element, such as a step changing from
    // a link to a button. Other added nodes are skipped so they don't snap the
    // scroller back to the active item.
    const observer = new MutationObserver(mutations => {
      const activeItemChanged = mutations.some(
        mutation =>
          mutation.type === "attributes" ||
          Array.from(mutation.addedNodes).some(isOrHoldsActiveItem)
      )
      if (activeItemChanged) scrollActiveItemClearOfFades()
    })
    observer.observe(scroller, {
      attributeFilter: [activeAttribute],
      childList: true,
      subtree: true
    })
    return () => observer.disconnect()
  }, [activeAttribute, disabled])

  return {
    scrollerProps: {
      ref: scrollerRef,
      "data-overflow-left": hasLeftOverflow ? "" : undefined,
      "data-overflow-right": hasRightOverflow ? "" : undefined,
      onFocus: disabled ? undefined : scrollFocusedItemIntoView
    },
    scrollButtonsProps: { scrollerRef, hasLeftOverflow, hasRightOverflow }
  }
}
